import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../context/CityContext';
import '../styles/CarHubLocations.css';

const CarHubLocations = () => {
  const navigate = useNavigate();
  const { selectedCity, setSelectedCity } = useCity();
  const [activeState, setActiveState] = useState('Madhya Pradesh');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);

  const locationData = {
    'Madhya Pradesh': [
      {
        city: 'Indore',
        hubs: [
          {
            name: 'Caryugo Car Hub, Treasure Island Mall',
            address: 'Ground Floor, Treasure Island Mall, AB Road, Indore - 452001',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 145,
            // image: '/images/hub-placeholder.jpg',
            type: 'premium'
          },
          {
            name: 'Caryugo Car Hub, Vijay Nagar',
            address: 'Plot No. 12, Scheme No. 54, Vijay Nagar, Indore - 452010',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 98,
            image: '/images/hub-placeholder.jpg',
            type: 'standard'
          }
        ]
      },
      {
        city: 'Mhow',
        hubs: [
          {
            name: 'Caryugo Car Hub, Mhow',
            address: 'Main Road, Near Bus Stand, Mhow - 453441',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 52,
            // image: '/images/hub-placeholder.jpg',
            type: 'standard'
          }
        ]
      },
      {
        city: 'Pithampur',
        hubs: [
          {
            name: 'Caryugo Car Hub, Pithampur Industrial Area',
            address: 'Sector 1, Pithampur Industrial Area, Pithampur - 454775',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 67,
            // image: '/images/hub-placeholder.jpg',
            type: 'standard'
          }
        ]
      },
      {
        city: 'Dewas',
        hubs: [
          {
            name: 'Caryugo Car Hub, Dewas',
            address: 'AB Road, Near Railway Station, Dewas - 455001',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 43,
            // image: '/images/hub-placeholder.jpg',
            type: 'standard'
          }
        ]
      },
      {
        city: 'Ujjain',
        hubs: [
          {
            name: 'Caryugo Car Hub, Ujjain',
            address: 'Dewas Road, Near Freeganj, Ujjain - 456010',
            hours: '10am - 8pm (Mon - Sun)',
            availableCars: 78,
            // image: '/images/hub-placeholder.jpg',
            type: 'standard'
          }
        ]
      }
    ]
  };

  const states = Object.keys(locationData);

  useEffect(() => {
    filterLocations();
  }, [searchQuery, activeState]);

  const filterLocations = () => {
    const stateData = locationData[activeState];
    if (!searchQuery.trim()) {
      setFilteredLocations(stateData);
      return;
    }

    const filtered = stateData.filter(location =>
      location.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.hubs.some(hub =>
        hub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hub.address.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
    setFilteredLocations(filtered);
  };

  const getTotalHubs = (state) => {
    return locationData[state].reduce((sum, city) => sum + city.hubs.length, 0);
  };

  const handleViewCars = (city) => {
    setSelectedCity(city);
    navigate(`/buy-cars?location=${city}`);
  };

  const handleVisitHub = (hub, city) => {
    // Open Google Maps with the address
    const address = encodeURIComponent(hub.address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  return (
    <div className="car-hub-locations">
      {/* Hero Section */}
      <section className="locations-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Car Hub Locations</h1>
          <p className="hero-subtitle">
            Test drive, learn about financing & more at your nearest car hub.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="locations-main">
        <div className="container">
          <div className="locations-grid">
            {/* Sidebar - States List */}
            <aside className="locations-sidebar">
              <h3 className="sidebar-title">Select State</h3>
              <div className="states-list">
                {states.map((state) => (
                  <button
                    key={state}
                    className={`state-item ${activeState === state ? 'active' : ''}`}
                    onClick={() => setActiveState(state)}
                  >
                    <div className="state-info">
                      <span className="state-name">{state}</span>
                      <span className="state-count">{getTotalHubs(state)} Car Hub{getTotalHubs(state) > 1 ? 's' : ''}</span>
                    </div>
                    {activeState === state && <span className="active-indicator">▶</span>}
                  </button>
                ))}
              </div>
            </aside>

            {/* Main Content - Locations Display */}
            <div className="locations-content">
              {/* Available Locations Header */}
              <div className="available-locations-header">
                <h2 className="section-title">
                  📍 AVAILABLE LOCATIONS IN {activeState.toUpperCase()}
                </h2>
                <div className="locations-count">
                  {filteredLocations.reduce((sum, loc) => sum + loc.hubs.length, 0)} Hub(s) Available
                </div>
              </div>

              {/* City Cards */}
              {filteredLocations.length > 0 ? (
                filteredLocations.map((location) => (
                  <div key={location.city} className="city-section">
                    <h3 className="city-title">
                      {location.hubs.length} Car Hub{location.hubs.length > 1 ? 's' : ''} in {location.city}
                    </h3>
                    
                    {/* Quick Access Buttons */}
                    <div className="city-quick-access">
                      <button className="city-badge available">
                        ✓ AVAILABLE
                      </button>
                      <button
                        className="view-cars-btn"
                        onClick={() => handleViewCars(location.city)}
                      >
                        View All Cars in {location.city}
                      </button>
                    </div>

                    {/* Hub Cards */}
                    <div className="hubs-grid">
                      {location.hubs.map((hub, idx) => (
                        <div key={idx} className={`hub-card ${hub.type}`}>
                          <div className="hub-image">
                            <img src={hub.image} alt={hub.name} />
                            {hub.type === 'premium' && (
                              <span className="premium-badge">⭐ Premium Hub</span>
                            )}
                            <div className="hub-overlay">
                              <button
                                className="visit-btn"
                                onClick={() => handleVisitHub(hub, location.city)}
                              >
                                Get Directions
                              </button>
                            </div>
                          </div>
                          
                          <div className="hub-details">
                            <h4 className="hub-name">{hub.name}</h4>
                            
                            <div className="hub-info">
                              <div className="info-item">
                                <span className="info-icon">📍</span>
                                <span className="info-text">{hub.address}</span>
                              </div>
                              
                              <div className="info-item">
                                <span className="info-icon">🕐</span>
                                <span className="info-text">{hub.hours}</span>
                              </div>
                              
                              <div className="info-item">
                                <span className="info-icon">🚗</span>
                                <span className="info-text">
                                  <strong>{hub.availableCars} cars</strong> available
                                </span>
                              </div>
                            </div>

                            <div className="hub-actions">
                              <button
                                className="action-btn primary"
                                onClick={() => handleViewCars(location.city)}
                              >
                                View {hub.availableCars} Cars
                              </button>
                              <button
                                className="action-btn secondary"
                                onClick={() => handleVisitHub(hub, location.city)}
                              >
                                Visit Hub
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>No locations found</h3>
                  <p>Try adjusting your search or select a different state</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="benefits-title">Why Visit Our Car Hubs?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🚗</div>
              <h3>Test Drive</h3>
              <p>Experience your dream car with a comprehensive test drive</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3>Instant Finance</h3>
              <p>Get instant car loans with 0 down payment options</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">✅</div>
              <h3>Quality Assured</h3>
              <p>200+ point inspection on every vehicle</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🤝</div>
              <h3>Expert Guidance</h3>
              <p>Professional consultation for your car buying journey</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarHubLocations;